import deLocale from "@fullcalendar/core/locales/de";
import timeGridPlugin from "@fullcalendar/timegrid";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import { useMasterDataQuery, queryKeys, fetchAllBookings } from "../../api";

export function useBaseCalendarConfig(
	handlers,
	queryClient,
	calendarFilters = null,
	masterData = null
) {
	const {
		handleEventAdd,
		handleEventRemove,
		handleDateSelect,
		handleEventClick,
		handleDatesChange,
		handleViewDidMount,
		handleEventDrop,
		handleEventResize,
	} = handlers;

	// Generate dynamic license key
	const currentYear = new Date().getFullYear();
	const jan1Timestamp = Math.floor(
		new Date(currentYear, 0, 1).getTime() / 1000
	);
	const schedulerLicenseKey = `1234567890-fcs-${jan1Timestamp}`;

	// Global device detection
	const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
	const isMobile =
		window.matchMedia("(pointer: coarse)").matches ||
		(window.innerWidth < 768 && hasTouch);

	// Helper functions for tooltip handling
	function showBookingTooltip(info) {
		const event = info.event;
		const booking = event.extendedProps.booking;

		if (!booking) return;

		// Remove existing tooltip
		hideBookingTooltip();

		// Get current user info for privacy checks
		const currentUserInfo = masterData?.masterData?.value?.userInfo;

		// Calculate if user can see all details
		const canViewDetails =
			masterData.canViewOtherBookings.value ||
			(booking.student &&
				booking.student?.id === currentUserInfo.student?.id) ||
			(booking.employee &&
				booking.employee?.id === currentUserInfo.employee?.id);

		// Create tooltip content with booking details
		let tooltipContent = "";

		// show usage type and, optionally, title
		const bookingType = masterData.getBookingTypeById.value(
			booking.bookingTypeId
		);
		if (booking.title) {
			tooltipContent +=
				`<div style="font-weight: bold; margin-bottom: 6px; color: #fff;">` +
				`${bookingType.title}: ${booking.title}</div>`;
		} else {
			tooltipContent +=
				`<div style="font-weight: bold; margin-bottom: 6px; color: #fff;">` +
				`${bookingType.title}</div>`;
		}

		// show person, if allowed
		if (canViewDetails) {
			if (booking.employee) {
				tooltipContent +=
					`<div style="margin-bottom: 4px; color: #e6e6e6;">` +
					`<i class="fa-solid fa-chalkboard-user" style="width: 25px;"></i>` +
					`${booking.employee.displayName}</div>`;
			} else if (booking.student) {
				tooltipContent +=
					`<div style="margin-bottom: 4px; color: #e6e6e6;">` +
					`<i class="fa-solid fa-graduation-cap" style="width: 25px;"></i>` +
					`${booking.student.displayName}</div>` +
					`<div style="margin-bottom: 4px; color: #e6e6e6;">` +
					`<i class="fa-solid fa-coins" style="width: 25px;"></i>` +
					(booking.points
						? `${booking.points} Punkte</div>`
						: booking.walkIn
							? `Sofortbuchung`
							: `Keine Punkte`) +
					`</div>`;
			} else {
				tooltipContent +=
					`<div style="margin-bottom: 4px; color: #e6e6e6;">` +
					`<i class="fa-solid fa-computer" style="width: 20px;"></i> Systembuchung</div>`;
			}
		}

		// Add time info
		const seriesIcon = booking.series ? " ⟲" : "";
		const startTime = new Date(event.start).toLocaleTimeString("de-DE", {
			hour: "2-digit",
			minute: "2-digit",
		});
		const endTime = new Date(event.end).toLocaleTimeString("de-DE", {
			hour: "2-digit",
			minute: "2-digit",
		});
		tooltipContent +=
			`<div style="margin-bottom: 4px; color: #ccc;">` +
			`<i class="fa-solid fa-clock" style="width: 25px;"></i>` +
			`${startTime} - ${endTime}${seriesIcon}</div>`;

		// Show description, if allowed and available
		if (canViewDetails && booking.description && booking.description.trim()) {
			tooltipContent +=
				`<div style="margin-top: 6px; padding-top: 6px; ` +
				`border-top: 1px solid #555; color: #ddd; font-style: italic;">` +
				`${booking.description.replace(/\n/g, "<br>")}</div>`;
		}

		// Create and position tooltip
		const tooltip = document.createElement("div");
		tooltip.id = "booking-tooltip";
		tooltip.innerHTML = tooltipContent;

		tooltip.style.cssText = `
			position: absolute;
			background: rgba(0, 0, 0, 0.95);
			color: white;
			padding: ${isMobile ? "12px 16px" : "10px 14px"};
			border-radius: 8px;
			font-size: ${isMobile ? "14px" : "13px"};
			line-height: 1.5;
			z-index: 1000;
			max-width: ${isMobile ? "280px" : "300px"};
			box-shadow: 0 4px 12px rgba(0,0,0,0.4);
			pointer-events: none;
			white-space: normal;
			word-wrap: break-word;
			font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
		`;

		// Position tooltip
		const rect = info.el.getBoundingClientRect();
		const viewportWidth = window.innerWidth;

		// Calculate initial position
		let left = rect.left + window.scrollX + rect.width / 2;
		let top = rect.top + window.scrollY - 10;

		document.body.appendChild(tooltip);

		// Adjust position to keep tooltip in viewport
		const tooltipRect = tooltip.getBoundingClientRect();

		// Horizontal adjustment
		if (left - tooltipRect.width / 2 < 10) {
			left = tooltipRect.width / 2 + 10;
		} else if (left + tooltipRect.width / 2 > viewportWidth - 10) {
			left = viewportWidth - tooltipRect.width / 2 - 10;
		}

		// Vertical adjustment - show below on mobile if not enough space above
		if (isMobile && top - tooltipRect.height < 10) {
			top = rect.bottom + window.scrollY + 10;
			tooltip.style.transform = "translateX(-50%)";
		} else {
			tooltip.style.transform = "translateX(-50%) translateY(-100%)";
		}

		tooltip.style.left = left + "px";
		tooltip.style.top = top + "px";
	}

	function hideBookingTooltip() {
		const tooltip = document.getElementById("booking-tooltip");
		if (tooltip) {
			tooltip.remove();
		}
	}

	function showConcertRequestTooltip(info) {
		const event = info.event;
		const cr = event.extendedProps?.concertRequest;

		if (!cr) return;

		hideBookingTooltip();

		const startTime = event.start
			? new Date(event.start).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
			: "";
		const endTime = event.end
			? new Date(event.end).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
			: "";

		let tooltipContent = "";

		tooltipContent +=
			`<div style="font-weight: bold; margin-bottom: 6px; color: #fff;">` +
			`Konzertanfrage: ${cr.title || ""}</div>`;

		if (cr.performer) {
			tooltipContent +=
				`<div style="margin-bottom: 4px; color: #e6e6e6;">` +
				`<i class="fa-solid fa-music" style="width: 25px;"></i>` +
				`${cr.performer}</div>`;
		}

		if (startTime && endTime) {
			tooltipContent +=
				`<div style="margin-bottom: 4px; color: #ccc;">` +
				`<i class="fa-solid fa-clock" style="width: 25px;"></i>` +
				`${startTime} - ${endTime}</div>`;
		}

		if (cr.preferredDate) {
			tooltipContent +=
				`<div style="margin-bottom: 4px; color: #e6e6e6;">` +
				`<i class="fa-solid fa-star" style="width: 25px;"></i>` +
				`Wunsch: ${new Date(cr.preferredDate).toLocaleDateString("de-DE")}</div>`;
		}
		if (cr.alternativeDate) {
			tooltipContent +=
				`<div style="margin-bottom: 4px; color: #e6e6e6;">` +
				`<i class="fa-solid fa-calendar-alt" style="width: 25px;"></i>` +
				`Alternative: ${new Date(cr.alternativeDate).toLocaleDateString("de-DE")}</div>`;
		}
		if (cr.fallbackDate) {
			tooltipContent +=
				`<div style="margin-bottom: 4px; color: #e6e6e6;">` +
				`<i class="fa-solid fa-calendar-check" style="width: 25px;"></i>` +
				`Ausweich: ${new Date(cr.fallbackDate).toLocaleDateString("de-DE")}</div>`;
		}

		if (cr.requestedBy) {
			tooltipContent +=
				`<div style="margin-bottom: 4px; color: #e6e6e6;">` +
				`<i class="fa-solid fa-user" style="width: 25px;"></i>` +
				`${cr.requestedBy}</div>`;
		}

		if (cr.note && cr.note.trim()) {
			const escaped = cr.note
				.replace(/&/g, "&amp;")
				.replace(/</g, "&lt;")
				.replace(/>/g, "&gt;")
				.replace(/\n/g, "<br>");

			tooltipContent +=
				`<div style="margin-top: 6px; padding-top: 6px; ` +
				`border-top: 1px solid #555; color: #ddd; font-style: italic;">` +
				`${escaped}</div>`;
		}

		const tooltip = document.createElement("div");
		tooltip.id = "booking-tooltip";
		tooltip.innerHTML = tooltipContent;

		tooltip.style.cssText = `
			position: absolute;
			background: rgba(0, 0, 0, 0.95);
			color: white;
			padding: ${isMobile ? "12px 16px" : "10px 14px"};
			border-radius: 8px;
			font-size: ${isMobile ? "14px" : "13px"};
			line-height: 1.5;
			z-index: 1000;
			max-width: ${isMobile ? "280px" : "320px"};
			box-shadow: 0 4px 12px rgba(0,0,0,0.4);
			pointer-events: none;
			white-space: normal;
			word-wrap: break-word;
			font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
		`;

		const rect = info.el.getBoundingClientRect();
		const viewportWidth = window.innerWidth;

		let left = rect.left + window.scrollX + rect.width / 2;
		let top = rect.top + window.scrollY - 10;

		document.body.appendChild(tooltip);

		const tooltipRect = tooltip.getBoundingClientRect();

		if (left - tooltipRect.width / 2 < 10) {
			left = tooltipRect.width / 2 + 10;
		} else if (left + tooltipRect.width / 2 > viewportWidth - 10) {
			left = viewportWidth - tooltipRect.width / 2 - 10;
		}

		if (isMobile && top - tooltipRect.height < 10) {
			top = rect.bottom + window.scrollY + 10;
			tooltip.style.transform = "translateX(-50%)";
		} else {
			tooltip.style.transform = "translateX(-50%) translateY(-100%)";
		}

		tooltip.style.left = left + "px";
		tooltip.style.top = top + "px";
	}

	return {
		schedulerLicenseKey,
		plugins: [
			timeGridPlugin,
			dayGridPlugin,
			interactionPlugin,
			resourceTimelinePlugin,
		],

		// Custom date picker button
		customButtons: {
			datePicker: {
				text: "📅 Datum",
				hint: "Zu Datum springen",
				click: function () {
					// Call the Vue showDatePicker function from handlers
					if (handlers.showDatePicker) {
						handlers.showDatePicker();
					}
				},
			},
		},

		// Custom day header content to handle styling
		dayHeaderContent: function (args) {
			const view = args.view;
			const date = args.date;
			const dayName = date.toLocaleDateString("de-DE", { weekday: "short" });
			const dayNumber = date.getDate();
			const monthNumber = date.getMonth() + 1;

			// Format date as YYYY-MM-DD using local timezone
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, "0");
			const day = String(date.getDate()).padStart(2, "0");
			const dateStr = `${year}-${month}-${day}`;

			// In week view, make dates clickable with custom click handler
			if (view.type === "timeGridWeek") {
				return {
					html: `<span class="fc-day-header-clickable" data-date="${dateStr}">
					${dayName}. ${dayNumber}.${monthNumber}</span>`,
				};
			}

			// In month view, just show weekday name
			if (view.type === "dayGridMonth") {
				return {
					html: `<span class="fc-day-header-text">
					${dayName}.</span>`,
				};
			}

			// In day view, remove links
			return {
				html: `<span class="fc-day-header-text">${dayName}. ${dayNumber}.${monthNumber}</span>`,
			};
		},

		dayCellContent: function (args) {
			const view = args.view;
			const date = args.date;

			// Format date as YYYY-MM-DD using local timezone
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, "0");
			const day = String(date.getDate()).padStart(2, "0");
			const dateStr = `${year}-${month}-${day}`;

			// In month view, just show weekday name
			if (view.type === "dayGridMonth") {
				return {
					html: `<span data-date="${dateStr}" class="fc-daygrid-day-number-clickable">${day}</span>`,
				};
			}

			return {
				domNodes: [],
			};
		},

		// Resources configuration for multi-room view
		resources: async function (fetchInfo, successCallback, failureCallback) {
			try {
				const masterData = await queryClient.fetchQuery({
					...useMasterDataQuery(),
					staleTime: Infinity, // Use cached data if available
				});

				let resources = [];
				let orderIndex = 0;

				// Iterate through buildings in the order they appear in the server response
				for (const buildingKey in masterData.buildings.list) {
					const building = masterData.buildings.list[buildingKey];
					// Keep rooms in the exact order from server
					building.rooms.forEach((room) => {
						resources.push({
							id: room.id.toString(),
							title: room.doorLabel + (room.name ? " " + room.name : ""),
							description: room.name,
							building:
								(building.short ? building.short + ": " : "") + building.name,
							buildingName: building.name,
							buildingId: building.id,
							orderIndex: orderIndex++, // Add order index to preserve server order
							extendedProps: {
								equipments: room.equipments,
								url: room.url,
								canCreateRegular: room.permissions.canCreateRegular,
								canCreateWithoutTransaction: room.permissions.canCreateWithoutTransaction,
								keyRequired: room.keyRequired,
							},
							// Add room data for filters
							permissions: room.permissions,
							equipments: room.equipments,
							roomTypes: room.roomTypes,
							seats: room.seats,
						});
					});
				}

				// Apply room filters if available
				if (calendarFilters?.applyRoomFilters) {
					resources = calendarFilters.applyRoomFilters(resources);
				}

				// Apply booking-based resource filtering if booking filters are active
				if (calendarFilters?.getRoomIdsWithFilteredBookings) {
					try {
						const date = fetchInfo.end?.toISOString().split("T")[0];
						const bookingsData = await queryClient.fetchQuery({
							queryKey: queryKeys.allBookings(date),
							queryFn: () => fetchAllBookings(date),
							staleTime: 1000 * 60 * 2, // 2 minutes
						});

						// Create minimal booking objects for filtering
						const bookingEvents = [];
						for (const building of Object.values(bookingsData.buildings)) {
							for (const room of Object.values(building.rooms)) {
								for (const booking of room.bookings) {
									bookingEvents.push({
										resourceId: booking.roomId.toString(),
										extendedProps: {
											bookingTypeId: booking.bookingTypeId,
											studentId: booking.student?.id,
											employeeId: booking.employee?.id,
										},
									});
								}
							}
						}

						const filteredRoomIds =
							calendarFilters.getRoomIdsWithFilteredBookings(bookingEvents);
						if (filteredRoomIds !== null) {
							if (filteredRoomIds.size > 0) {
								resources = resources.filter((resource) =>
									filteredRoomIds.has(resource.id)
								);
							} else {
								// No rooms have bookings matching the filters - show no resources
								resources = [];
							}
						}
					} catch (error) {
						console.warn(
							"Could not apply booking-based resource filtering:",
							error
						);
						// Continue with unfiltered resources
					}
				}

				successCallback(resources);
			} catch (error) {
				console.error("Failed to fetch calendar resources:", error);
				failureCallback(error);
			}
		},
		resourceGroupField: "building",
		resourceOrder: "orderIndex", // Use our custom order index to preserve server order
		resourceLabelClassNames: function (arg) {
			return arg.resource.extendedProps.canCreateRegular
				? "bookable-resource"
				: "non-bookable-resource";
		},

		// Event lifecycle callbacks
		eventAdd: handleEventAdd,
		eventRemove: handleEventRemove,

		// User interaction callbacks
		select: handleDateSelect,
		eventClick: handleEventClick,
		eventDrop: handleEventDrop,
		eventResize: handleEventResize,
		datesSet: handleDatesChange,

		// Mobile and desktop tooltip functionality
		eventMouseEnter: function (info) {
			if (isMobile) {
				return;
			}

			if (info.event.extendedProps?.type === "concert-request") {
				showConcertRequestTooltip(info);
				return;
			}

			showBookingTooltip(info);
		},

		eventMouseLeave: function () {
			if (isMobile) {
				return;
			}

			hideBookingTooltip();
		},

		// Mobile: Show tooltip on single tap (but don't prevent event click)
		eventDidMount: function (info) {
			const eventEl = info.el;
			const event = info.event;

			if (event.extendedProps.isHoliday) {
				eventEl.setAttribute("title", event.extendedProps.holidayName);
				return;
			}

			const booking = event.extendedProps.booking;

			if (!booking) return;

			// Add mobile tooltip handling only on touch devices
			if (hasTouch) {
				let touchStartTime = 0;
				let tooltipTimer = null;

				eventEl.addEventListener("touchstart", function () {
					touchStartTime = Date.now();
					// Show tooltip after short delay to distinguish from scroll/swipe
					tooltipTimer = setTimeout(() => {
						showBookingTooltip(info);
					}, 200);
				});

				eventEl.addEventListener("touchmove", function () {
					// Cancel tooltip if user is scrolling
					if (tooltipTimer) {
						clearTimeout(tooltipTimer);
						tooltipTimer = null;
					}
					hideBookingTooltip();
				});

				eventEl.addEventListener("touchend", function () {
					const touchDuration = Date.now() - touchStartTime;

					if (tooltipTimer) {
						clearTimeout(tooltipTimer);
						tooltipTimer = null;
					}

					// If it was a quick tap (not a long press), show tooltip briefly
					if (touchDuration < 300) {
						showBookingTooltip(info);
						// Auto-hide after 3 seconds on mobile
						setTimeout(() => {
							hideBookingTooltip();
						}, 3000);
					}
				});
			}
		},

		// Behavior
		selectable: true,
		selectMirror: true,
		editable: false,
		eventStartEditable: false,
		eventDurationEditable: false,
		eventResizableFromStart: true,

		// Mobile touch support
		longPressDelay: 300,
		selectLongPressDelay: 150,
		eventLongPressDelay: 250,
		selectMinDistance: 5,

		// Localization
		locales: [deLocale],
		locale: "de",
		firstDay: 1,

		// Time formatting - standardize to hh:mm format
		slotLabelFormat: {
			hour: "2-digit",
			minute: "2-digit",
			omitZeroMinute: false,
			meridiem: false,
		},
		eventTimeFormat: {
			hour: "2-digit",
			minute: "2-digit",
			omitZeroMinute: false,
			meridiem: false,
		},

		// Styling
		nowIndicator: true,
		themeSystem: "bootstrap5",
		height: "auto",
		aspectRatio: 1.35,
		handleWindowResize: true,
		windowResizeDelay: 100,
		viewDidMount: function (info) {
			handleViewDidMount(info);
			const calendarEl = info.el;

			// Add global touch handler to hide tooltips on mobile when touching outside
			if (hasTouch) {
				const globalTouchHandler = function (e) {
					const tooltip = document.getElementById("booking-tooltip");
					if (tooltip && !e.target.closest(".fc-event")) {
						hideBookingTooltip();
					}
				};

				// Remove existing listener if it exists
				if (calendarEl._globalTouchHandler) {
					document.removeEventListener(
						"touchstart",
						calendarEl._globalTouchHandler
					);
				}

				// Add new listener and store reference
				calendarEl._globalTouchHandler = globalTouchHandler;
				document.addEventListener("touchstart", globalTouchHandler);
			}
		},
	};
}
